import { motion } from "framer-motion";

const steps = [
  {
    n: "01",
    title: "Break It Down",
    body: "We break down complex words syllable by syllable, so nothing feels overwhelming.",
  },
  {
    n: "02",
    title: "Isolate The Sound",
    body: "Every sound gets connected to a word you already know how to say.",
  },
  {
    n: "03",
    title: "Build It Back Up",
    body: "We build the pieces back together, one rung at a time, until they click.",
  },
  {
    n: "04",
    title: "Master The Rhythm",
    body: "Natural rhythm and intonation come last, until you sound like a native speaker.",
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] },
  }),
};

export const Manifesto = () => {
  return (
    <section
      id="about"
      data-testid="manifesto-section"
      className="relative w-full px-4 py-24 sm:px-8 sm:py-32"
    >
      <div className="mx-auto max-w-5xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7 }}
          className="mx-auto max-w-2xl text-center"
        >
          <span className="font-mono-accent text-xs font-semibold uppercase tracking-[0.25em] text-sky-600">
            The Method
          </span>
          <h2 className="font-display mt-4 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl lg:text-5xl">
            Mastering pronunciation, made simple &amp; fun.
          </h2>
          <p className="font-body mt-5 text-base leading-relaxed text-slate-500 sm:text-lg">
            The Sound Ladder makes mastering English pronunciation simple and fun. We
            break down complex words syllable by syllable, isolating each sound by
            connecting it to words you already know. We build the pieces back
            together, including the natural rhythm and intonation, until you sound
            like a native speaker.
          </p>
        </motion.div>

        <div className="mt-16 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {steps.map((step, i) => (
            <motion.div
              key={step.n}
              data-testid={`manifesto-step-${step.n}`}
              custom={i}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-60px" }}
              variants={fadeUp}
              className="group relative rounded-3xl border border-slate-200/70 bg-white/70 p-6 shadow-sm shadow-sky-950/5 backdrop-blur-sm transition-all duration-300 hover:-translate-y-1.5 hover:border-sky-200 hover:shadow-[0_20px_45px_-15px_rgba(2,132,199,0.2)]"
            >
              <span className="font-display text-4xl font-extrabold text-sky-100 transition-colors duration-300 group-hover:text-sky-200">
                {step.n}
              </span>
              <h3 className="font-display mt-3 text-lg font-bold text-slate-900">
                {step.title}
              </h3>
              <p className="font-body mt-2 text-sm leading-relaxed text-slate-500">
                {step.body}
              </p>
              {i < steps.length - 1 && (
                <div className="absolute -right-3 top-1/2 hidden h-px w-6 -translate-y-1/2 bg-slate-300 lg:block" />
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
